import { tKey } from 'ember-intl';
import {
  type Kpi,
  KPIS,
  type MetricType,
} from 'lumen/models/market-performance/data-item';
import {
  LOCAL_STORAGE_KEYS,
  type StorageKey,
} from 'lumen/services/local-storage';

import { isVariance, type Variance, VARIANCES } from './variance';

export const SDLY_COLUMNS = {
  SDLY: 'sdly',
  NET_SDLY: 'net_sdly',
} as const;

export type SdlyColumn = (typeof SDLY_COLUMNS)[keyof typeof SDLY_COLUMNS];

export function isSdlyColumn(value: unknown): value is SdlyColumn {
  return Object.values(SDLY_COLUMNS).includes(value as SdlyColumn);
}

export const FORECAST_COLUMNS = {
  BI: 'bi_forecast',
  USER: 'user_forecast',
  RMS: 'rms_forecast',
  FINANCE: 'finance_forecast',
} as const;

export type ForecastColumn =
  (typeof FORECAST_COLUMNS)[keyof typeof FORECAST_COLUMNS];

export function isForecastColumn(value: unknown): value is ForecastColumn {
  return Object.values(FORECAST_COLUMNS).includes(value as ForecastColumn);
}

export const GROUP_COLUMNS_BY = {
  CATEGORIES: 'categories',
  KPIS: 'kpis',
} as const;

export type GroupColumnsBy =
  (typeof GROUP_COLUMNS_BY)[keyof typeof GROUP_COLUMNS_BY];

export function isGroupColumnsBy(value: unknown): value is GroupColumnsBy {
  return Object.values(GROUP_COLUMNS_BY).includes(value as GroupColumnsBy);
}

export const GROUP_BY = {
  MONTH: 'month',
  PROPERTY: 'property',
} as const;

export type GroupBy = (typeof GROUP_BY)[keyof typeof GROUP_BY];

export function isGroupBy(value: unknown): value is GroupBy {
  return Object.values(GROUP_BY).includes(value as GroupBy);
}

export const SHOW_AS = {
  CHART: 'chart',
  TABLE: 'table',
} as const;

export type ShowAs = (typeof SHOW_AS)[keyof typeof SHOW_AS];

export function isShowAs(value: unknown): value is ShowAs {
  return Object.values(SHOW_AS).includes(value as ShowAs);
}

export const SORT_ON = {
  ACTUALS: 'actuals',
  VARIANCE: 'variance',
} as const;

export type SortOn = (typeof SORT_ON)[keyof typeof SORT_ON];

export function isSortOn(value: unknown): value is SortOn {
  return Object.values(SORT_ON).includes(value as SortOn);
}

export const OVERVIEW_KPI_NAMES: readonly Kpi[] = [
  'occupancy',
  'rooms',
  'adr',
  'revenue',
];

export interface DataPreferences {
  forecastColumn: ForecastColumn;
  sdlyColumn: SdlyColumn;
  variance: Variance;
}

export const DEFAULT_DATA_PREFERENCES: Readonly<DataPreferences> =
  Object.freeze({
    forecastColumn: FORECAST_COLUMNS.BI,
    sdlyColumn: SDLY_COLUMNS.SDLY,
    variance: VARIANCES.NONE,
  });

export function parseDataPreferences(json: unknown): DataPreferences {
  if (typeof json !== 'object' || json === null) {
    return DEFAULT_DATA_PREFERENCES;
  }

  const raw = json as Record<string, unknown>;

  return {
    forecastColumn: isForecastColumn(raw.forecastColumn)
      ? raw.forecastColumn
      : DEFAULT_DATA_PREFERENCES.forecastColumn,
    sdlyColumn: isSdlyColumn(raw.sdlyColumn)
      ? raw.sdlyColumn
      : DEFAULT_DATA_PREFERENCES.sdlyColumn,
    variance: isVariance(raw.variance)
      ? raw.variance
      : DEFAULT_DATA_PREFERENCES.variance,
  };
}

export interface TableLayoutPreferences {
  groupColumnsBy: GroupColumnsBy;
  sortOn: SortOn;
}

export const DEFAULT_TABLE_LAYOUT_PREFERENCES: Readonly<TableLayoutPreferences> =
  Object.freeze({
    groupColumnsBy: GROUP_COLUMNS_BY.CATEGORIES,
    sortOn: SORT_ON.ACTUALS,
  });

export function parseTableLayoutPreferences(
  json: unknown,
): TableLayoutPreferences {
  if (typeof json !== 'object' || json === null) {
    return DEFAULT_TABLE_LAYOUT_PREFERENCES;
  }

  const raw = json as Record<string, unknown>;

  return {
    groupColumnsBy: isGroupColumnsBy(raw.groupColumnsBy)
      ? raw.groupColumnsBy
      : DEFAULT_TABLE_LAYOUT_PREFERENCES.groupColumnsBy,
    sortOn: isSortOn(raw.sortOn)
      ? raw.sortOn
      : DEFAULT_TABLE_LAYOUT_PREFERENCES.sortOn,
  };
}

export const METRIC_TYPE_TRANSLATIONS: Record<MetricType, string> = {
  bi_forecast: tKey('general.bi_forecast'),
  budget: tKey('general.budget'),
  finance_forecast: tKey('market.snapshots.metric_type.finance_forecast'),
  ly: tKey('market.snapshots.metric_type.total_ly'),
  net_sdly: tKey('market.snapshots.metric_type.net_sdly'),
  otb: tKey('general.otb'),
  rms_forecast: tKey('market.snapshots.metric_type.rms_forecast'),
  sdly: tKey('market.snapshots.metric_type.sdly'),
  user_forecast: tKey('market.snapshots.metric_type.user_forecast'),
};

// Any metric type the KPIS values can be compared against.
export type ComparisonMetricType = Exclude<MetricType, 'otb'>;

// Reorderable/hideable overview columns (OTB is always first). Ids are slots,
// not metrics: `sdly` and `forecast` resolve through the data preferences.
export const OVERVIEW_COLUMNS = ['sdly', 'ly', 'forecast', 'budget'] as const;

export type OverviewColumn = (typeof OVERVIEW_COLUMNS)[number];

export function getOverviewColumnMetrics(
  preferences: DataPreferences,
): Record<OverviewColumn, MetricType> {
  return {
    sdly: preferences.sdlyColumn,
    ly: 'ly',
    forecast: preferences.forecastColumn,
    budget: 'budget',
  };
}

export const KPI_TRANSLATIONS: Record<Kpi, string> = {
  adr: tKey('general.adr'),
  occupancy: tKey('market.snapshots.columns.occ'),
  revenue: tKey('general.revenue'),
  revpar: tKey('general.revpar'),
  rooms: tKey('market.snapshots.columns.rms'),
};

// Snapshot views; the id is the URL slug and what the view switcher selects.
export const SNAPSHOT_VIEWS = [
  {
    id: 'overview',
    label: tKey('market.snapshots.view.overview'),
    route: 'market.snapshots.overview',
  },
] as const;

export type SnapshotView = (typeof SNAPSHOT_VIEWS)[number]['id'];

/** The reorderable/hideable columns of a view and where their settings are stored. */
export interface SnapshotViewColumnsConfig {
  /** Always-first metric outside `ids`; can't be moved or hidden. Without it, one column must stay visible. */
  fixed?: MetricType;
  ids: readonly string[];
  /** The KPIs the view shows, in their default order. */
  kpis: readonly Kpi[];
  /** Resolves column slots to the metrics they show for the given preferences. */
  metrics: (preferences: DataPreferences) => Record<string, MetricType>;
  storageKeys: { columns: StorageKey; kpiColumns: StorageKey };
}

/** Everything that differs between snapshot views; routes and controllers read the rest from here. */
export interface SnapshotViewConfig {
  api: { compare: (preferences: DataPreferences) => string; view: string };
  columns: SnapshotViewColumnsConfig;
  id: string;
  /** The KPI cards above the table, in order. */
  kpiCards: readonly Kpi[];
}

// `satisfies` keeps each view's own (literal) shape.
export const SNAPSHOT_VIEW_CONFIG = {
  overview: {
    api: {
      compare: ({ forecastColumn, sdlyColumn }): string =>
        `${sdlyColumn},ly,${forecastColumn},budget`,
      view: 'overview',
    },
    columns: {
      fixed: 'otb',
      ids: OVERVIEW_COLUMNS,
      kpis: KPIS,
      metrics: getOverviewColumnMetrics,
      storageKeys: {
        columns: LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_OVERVIEW_COLUMNS,
        kpiColumns: LOCAL_STORAGE_KEYS.MARKET_SNAPSHOTS_OVERVIEW_KPI_COLUMNS,
      },
    },
    id: 'overview',
    kpiCards: OVERVIEW_KPI_NAMES,
  },
} satisfies Record<string, SnapshotViewConfig>;
